/**
 * SupportAtHomeHelpers.js – Shared constants and helpers for Support at Home flow.
 * Used by SupportAtHomePage, StrategiesSection, and ServiceBuilderSection.
 */
import { PENSION_RATES } from '../../constants/index.ts';
import {
    computeDisposalCgt,
    taxContextFromAssessableIncomes,
    type DisposalTaxContext,
} from '../../../../shared/src/calculation/disposalCgt.ts';
import { applyDownsizeToFactFind } from './supportAtHomeFactFindMutators.tsx';
import {
    debitCgtFromOwnershipPool,
    findPersonalBankAccount,
} from '../../utils/entityFundingUtils.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFindAsset, AssetType } from '../../../../shared/src/calculation/factFindTypes.ts';
import { INVESTMENT_PROFILES, ASSET_CLASS_ASSUMPTIONS } from '../../../../shared/src/calculation/investmentProfiles.ts';

type DraftAsset = FactFindAsset;

export { applyDownsizeToFactFind };

// Re-export centrally maintained Age Pension rates for Support at Home flows.
export { PENSION_RATES };

export const DOWNSIZER_CONTRIBUTION_CAP = 300000;

// Re-export the shared ui CurrencyInput so existing `from './SupportAtHomeHelpers.tsx'` imports
// keep working — this used to be its own hand-rolled duplicate.
export { default as CurrencyInput } from '../../components/ui/CurrencyInput.tsx';

/** Calc-engine `financials` result bag — only the two fields this helper reads (see calculationApi.ts's `Financials = Record<string, unknown>`). */
export interface FinancialsForCgt {
    clientAssessableIncome?: number;
    partnerAssessableIncome?: number;
    /** Household tax position for the year, used to tax a capital gain — see disposalCgt.ts. */
    taxContext?: DisposalTaxContext;
    [key: string]: unknown;
}

/**
 * CGT on an asset sale funding a Support at Home strategy.
 * Delegates to the single shared CGT engine (shared/src/calculation/disposalCgt.ts) — this previously
 * held a fourth copy of the same math.
 */
export const calculateCgtForStrategy = (asset: DraftAsset, saleAmount: number, beforeFinancials: FinancialsForCgt | undefined): number => {
    const value = parseFloat(String(asset.value)) || 0;
    if (!value || saleAmount <= 0) return 0;

    // `taxContext` comes from calculateFinancials; the fallback covers a payload predating it.
    const taxContext =
        beforeFinancials?.taxContext ??
        taxContextFromAssessableIncomes(
            beforeFinancials?.clientAssessableIncome || 0,
            beforeFinancials?.partnerAssessableIncome || 0,
            asset.ownership === 'joint' || asset.ownership === 'partner',
        );

    // DraftAsset is structurally compatible with FactFindAsset (see types/formDraft.ts); the shared
    // calc-engine fn defensively coerces internally regardless, so this cast is safe (see
    // factFindTypes.ts header comment).
    return computeDisposalCgt({
        taxContext,
        asset: asset as unknown as FactFindAsset,
        salePrice: saleAmount,
        // Brokerage on a share sale is immaterial at this level of estimate.
        sellingCosts: 0,
        originalAssetValue: value,
    }).estimatedCGT;
};

// applyDownsizeToFactFind — see supportAtHomeFactFindMutators.js (re-exported above)

/** A single asset earmarked to fund a renovate/upgrade property strategy. */
export interface PropertyStrategyFundingSource {
    assetId: string | number;
    assetType: string;
    amount: number | string;
}

export interface RenovateDetails {
    renovationAmount: number | string;
    fundingSources?: PropertyStrategyFundingSource[];
    [key: string]: unknown;
}

export interface UpgradeDetails {
    currentHomeValue?: number | string;
    newHomePurchasePrice: number | string;
    sellingCosts?: number | string;
    fundingSources?: PropertyStrategyFundingSource[];
    [key: string]: unknown;
}

/**
 * Sells the nominated funding assets for a renovate/upgrade and returns what they actually raised.
 *
 * WHAT IS RAISED, NOT WHAT WAS ASKED (25 Oct 2026). A source's request is capped at what the account
 * holds now, and a source that is no longer there — an account the linked aged care plan has already
 * spent on its deposit — raises nothing. The old code counted the request in full either way, so a
 * plan funded from accounts the deposit had emptied added the whole renovation to the home while
 * taking almost nothing out: net worth rose by money nobody had (+$375,000 on a $416,000 renovation).
 */
const sellFundingSources = (modified: DraftFactFind, fundingSources: PropertyStrategyFundingSource[], need: number, beforeFinancials: FinancialsForCgt | undefined): number => {
    let raised = 0;
    for (const funding of fundingSources) {
        if (raised >= need) break;
        const amount = parseFloat(String(funding.amount)) || 0;
        if (amount <= 0) continue;
        const assetList = modified.assets?.[funding.assetType];
        if (!assetList || !Array.isArray(assetList)) continue;
        const asset = assetList.find(a => String(a.id) === String(funding.assetId));
        if (!asset) continue;
        const assetValue = parseFloat(String(asset.value)) || 0;
        // Nothing is sold beyond what the work still needs, so an over-full allocation loses nothing.
        const saleAmount = Math.min(amount, assetValue, need - raised);
        if (saleAmount <= 0) continue;

        if (['shares', 'investmentProperty'].includes(funding.assetType) && asset.costBase) {
            const cgt = calculateCgtForStrategy(asset, saleAmount, beforeFinancials);
            debitCgtFromOwnershipPool(modified.assets, cgt, asset.ownership);
        }

        const newValue = Math.max(0, assetValue - saleAmount);
        if (newValue > 0) {
            asset.value = newValue;
        } else {
            assetList.splice(assetList.indexOf(asset), 1);
        }
        raised += saleAmount;
    }
    return raised;
};

/**
 * Takes what the named sources did not cover from the bank — the personal account first, then the
 * others — and returns what it could take. Never more than the accounts hold, so nothing is spent
 * that is not there.
 */
const takeFromBanks = (modified: DraftFactFind, amount: number): number => {
    const want = Math.max(0, amount);
    const banks = modified.assets?.bankAccounts;
    if (want <= 0 || !Array.isArray(banks) || banks.length === 0) return 0;
    const personal = findPersonalBankAccount(modified.assets);
    const order = personal ? [personal, ...banks.filter(b => b !== personal)] : [...banks];
    let taken = 0;
    for (const bank of order) {
        if (taken >= want) break;
        const held = Math.max(0, parseFloat(String(bank.value)) || 0);
        const take = Math.min(held, want - taken);
        bank.value = held - take;
        taken += take;
    }
    return taken;
};

// Apply renovate strategy to factFind. The home gains what was spent on it — what the sources and the
// bank could actually pay — so net worth does not move (CGT aside).
export const applyRenovateToFactFind = (factFind: DraftFactFind, details: RenovateDetails, beforeFinancials: FinancialsForCgt | undefined = {}): DraftFactFind => {
    const modified: DraftFactFind = structuredClone(factFind);
    const renovationAmount = parseFloat(String(details.renovationAmount)) || 0;
    if (renovationAmount <= 0) return modified;

    const raised = sellFundingSources(modified, details.fundingSources || [], renovationAmount, beforeFinancials);
    const spent = raised + takeFromBanks(modified, renovationAmount - raised);

    if (modified.home) {
        const currentHomeValue = parseFloat(String(modified.home.value)) || 0;
        modified.home.value = currentHomeValue + spent;
    } else {
        modified.home = { value: spent };
    }

    return modified;
};

// Apply upgrade home strategy to factFind. The new home is worth the sale's net proceeds plus what the
// sources and the bank could add — never more than was paid for it — so net worth falls by the selling
// costs and nothing else.
export const applyUpgradeToFactFind = (factFind: DraftFactFind, details: UpgradeDetails, beforeFinancials: FinancialsForCgt | undefined = {}): DraftFactFind => {
    const modified: DraftFactFind = structuredClone(factFind);
    const currentHomeValue = parseFloat(String(details.currentHomeValue || factFind.home?.value)) || 0;
    const newHomePurchasePrice = parseFloat(String(details.newHomePurchasePrice)) || 0;
    const sellingCosts = parseFloat(String(details.sellingCosts)) || 0;

    if (newHomePurchasePrice <= currentHomeValue) return modified;

    const netProceedsFromSale = Math.max(0, currentHomeValue - sellingCosts);
    const additionalFundsNeeded = newHomePurchasePrice - netProceedsFromSale;

    if (additionalFundsNeeded <= 0) {
        if (modified.home) {
            modified.home.value = newHomePurchasePrice;
        } else {
            modified.home = { value: newHomePurchasePrice };
        }

        const surplus = Math.abs(additionalFundsNeeded);
        if (surplus > 0 && modified.assets?.bankAccounts && modified.assets.bankAccounts.length > 0) {
            const firstBankAccount = modified.assets.bankAccounts[0];
            const currentBalance = parseFloat(String(firstBankAccount.value)) || 0;
            firstBankAccount.value = currentBalance + surplus;
        }
        return modified;
    }

    const raised = sellFundingSources(modified, details.fundingSources || [], additionalFundsNeeded, beforeFinancials);
    const added = raised + takeFromBanks(modified, additionalFundsNeeded - raised);
    const paid = netProceedsFromSale + added;

    if (modified.home) {
        modified.home.value = paid;
    } else {
        modified.home = { value: paid };
    }

    return modified;
};

export interface SupportAssetOption {
    id: AssetType;
    label: string;
    defaults: {
        incomeRate: number;
        growthRate: number;
        frankingCredit?: number;
    };
}

export const SUPPORT_ASSET_OPTIONS: SupportAssetOption[] = [
    // Property getters throughout so a licensee's capital market assumptions apply at use time.
    { id: 'bankAccounts', label: 'Bank account', defaults: { get incomeRate() { return ASSET_CLASS_ASSUMPTIONS.bankIncomePct; }, growthRate: 0 } },
    { id: 'termDeposits', label: 'Term deposit', defaults: { get incomeRate() { return ASSET_CLASS_ASSUMPTIONS.termDepositIncomePct; }, growthRate: 0 } },
    { id: 'shares', label: 'Shares / Managed funds', defaults: { get incomeRate() { return ASSET_CLASS_ASSUMPTIONS.sharesIncomePct; }, get growthRate() { return ASSET_CLASS_ASSUMPTIONS.sharesGrowthPct; }, get frankingCredit() { return ASSET_CLASS_ASSUMPTIONS.sharesFrankingPct; } } },
    // Balanced presets from the one published table — property getters so the rate is read at USE
    // time and a licensee's own capital market assumptions apply (see investmentProfiles.ts).
    { id: 'superannuation', label: 'Superannuation', defaults: { incomeRate: 0, get growthRate() { return INVESTMENT_PROFILES.balanced.accumulationNetPct; } } },
    { id: 'accountBasedPensions', label: 'Account-based pension', defaults: { incomeRate: 0, get growthRate() { return INVESTMENT_PROFILES.balanced.pensionNetPct; } } },
    { id: 'investmentProperty', label: 'Investment property', defaults: { get incomeRate() { return ASSET_CLASS_ASSUMPTIONS.propertyYieldPct; }, get growthRate() { return ASSET_CLASS_ASSUMPTIONS.propertyGrowthPct; } } },
    { id: 'personalAssets', label: 'Personal asset', defaults: { incomeRate: 0, growthRate: 0 } },
    { id: 'other', label: 'Other asset', defaults: { incomeRate: 0, growthRate: 0 } }
];
